import { defineStore } from 'pinia';

const USER_ROLE_QUERY = `
  query {
    user {
      isAdmin
      isSuperuser
    }
  }
`;

type AuthRoleCache = {
  token: string;
  expiresAt: number;
};

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: '' as string,
    refreshToken: '' as string,
    username: '' as string,
    email: '' as string,
    isAdmin: false,
    isSuperuser: false,
    hydrated: false,
    roleCache: null as AuthRoleCache | null,
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.token),
    canAccessAdmin: (state) => state.isAdmin || state.isSuperuser,
  },
  actions: {
    // Keep localStorage only as persistence, while runtime checks use store state
    hydrateFromStorage() {
      this.token = localStorage.getItem('jwt_access_token') || '';
      this.refreshToken = localStorage.getItem('jwt_refresh_token') || '';
      this.username = localStorage.getItem('contrak_username') || '';
      this.email = localStorage.getItem('contrak_email') || '';
      this.isAdmin = localStorage.getItem('contrak_is_admin') === 'true';
      this.isSuperuser = localStorage.getItem('contrak_is_superuser') === 'true';
      this.hydrated = true;
    },
    setSession(payload: {
      accessToken?: string;
      refreshToken?: string;
      username?: string;
      email?: string;
      isAdmin?: boolean;
      isSuperuser?: boolean;
    }) {
      this.token = payload.accessToken || '';
      this.refreshToken = payload.refreshToken || '';
      this.username = payload.username || '';
      this.email = payload.email || '';
      this.isAdmin = payload.isAdmin === true;
      this.isSuperuser = payload.isSuperuser === true;
      this.hydrated = true;

      localStorage.setItem('jwt_access_token', this.token);
      localStorage.setItem('jwt_refresh_token', this.refreshToken);
      localStorage.setItem('contrak_username', this.username);
      localStorage.setItem('contrak_email', this.email);
      localStorage.setItem('contrak_is_admin', this.isAdmin ? 'true' : 'false');
      localStorage.setItem('contrak_is_superuser', this.isSuperuser ? 'true' : 'false');
      localStorage.setItem('last_activity', Date.now().toString());
    },
    clearSession() {
      this.token = '';
      this.refreshToken = '';
      this.username = '';
      this.email = '';
      this.isAdmin = false;
      this.isSuperuser = false;
      this.roleCache = null;
      this.hydrated = true;

      localStorage.removeItem('jwt_access_token');
      localStorage.removeItem('jwt_refresh_token');
      localStorage.removeItem('contrak_username');
      localStorage.removeItem('contrak_email');
      localStorage.removeItem('contrak_is_admin');
      localStorage.removeItem('contrak_is_superuser');
      localStorage.removeItem('last_activity');
    },
    setUsername(username: string) {
      this.username = username || '';
      localStorage.setItem('contrak_username', this.username);
    },
    // Activity API: keep all last_activity access centralized in this store
    updateLastActivity() {
      if (!this.isAuthenticated) return;
      localStorage.setItem('last_activity', Date.now().toString());
    },
    getLastActivity(): number | null {
      const value = localStorage.getItem('last_activity');
      if (!value) return null;
      const timestamp = parseInt(value, 10);
      return Number.isNaN(timestamp) ? null : timestamp;
    },
    isSessionExpired(maxIdleMs: number): boolean {
      if (!this.isAuthenticated) return false;
      const lastActivity = this.getLastActivity();
      if (!lastActivity) return false;
      return Date.now() - lastActivity > maxIdleMs;
    },
    async refreshRoles(force = false): Promise<boolean> {
      if (!this.token) {
        this.isAdmin = false;
        this.isSuperuser = false;
        return false;
      }

      const now = Date.now();
      if (
        !force
        && this.roleCache
        && this.roleCache.token === this.token
        && this.roleCache.expiresAt > now
      ) {
        return this.canAccessAdmin;
      }

      try {
        const response = await fetch(process.env.VUE_APP_APOLLO_URL as string, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ query: USER_ROLE_QUERY }),
        });

        if (!response.ok) {
          return this.canAccessAdmin;
        }

        const payload = await response.json();
        const isAdmin = payload?.data?.user?.isAdmin === true;
        const isSuperuser = payload?.data?.user?.isSuperuser === true;
        this.isAdmin = isAdmin;
        this.isSuperuser = isSuperuser;

        localStorage.setItem('contrak_is_admin', isAdmin ? 'true' : 'false');
        localStorage.setItem('contrak_is_superuser', isSuperuser ? 'true' : 'false');
        this.roleCache = {
          token: this.token,
          expiresAt: now + 60_000,
        };

        return this.canAccessAdmin;
      } catch (error) {
        console.warn('Unable to refresh user roles from server.', error);
        return this.canAccessAdmin;
      }
    },
  },
});
